import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

import { useAppDispatch } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { TextField } from 'components/Fields'

import { RenameDomainFormContent } from './styles'
import { renameDomainMessages } from './messages'
import { DOMAIN_API_ERROR } from 'features/jobs/consts'
import { getDomainSubDomainsThunk } from 'components/DomainBreadcrumbs/slice'
import {
  fetchSelectedNavDomainThunk,
  renameDomainThunk,
} from 'features/domains/slice'
import type { DomainType } from 'features/domains/types'
import domainOrJobNameValidator from 'app/utils/domainOrJobNameValidator'

const NAME_FIELD = 'domainName'

interface RenameDomainModalProps {
  domain: DomainType
  onClose: () => void
}

const RenameDomainModal = ({ domain, onClose }: RenameDomainModalProps) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const initialValues = {
    domainName: domain.name,
  }

  const schema = Yup.object().shape({
    [NAME_FIELD]: domainOrJobNameValidator(formatMessage),
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async ({ domainName }, formProps) => {
        try {
          await dispatch(
            renameDomainThunk({
              domainId: domain.id,
              name: domainName,
            })
          )
          await dispatch(
            getDomainSubDomainsThunk({
              domainId: domain.id,
              forceUpdate: true,
            })
          )
          await dispatch(fetchSelectedNavDomainThunk(domain.id))
          onClose()
        } catch (err: any) {
          console.log('Error renaming Domain/Group', err)

          if (err?.name === DOMAIN_API_ERROR.DOMAIN_JOB_EXISTS) {
            return formProps.setFieldError(
              NAME_FIELD,
              formatMessage(renameDomainMessages.alreadyExists)
            )
          }
          return formProps.setFieldError(
            NAME_FIELD,
            formatMessage(commonMessages.commonError)
          )
        }
      }}
    >
      {(formikProps) => {
        const isSameDomainName = formikProps.values.domainName === domain.name
        return (
          <ConfirmModal
            isOpen
            onDiscard={onClose}
            title={
              <FormattedMessage
                {...renameDomainMessages.title}
                values={{
                  domainName: domain.name,
                }}
              />
            }
            discardBtnTitle={<FormattedMessage {...commonMessages.cancel} />}
            confirmBtnTitle={<FormattedMessage {...commonMessages.save} />}
            onConfirm={formikProps.submitForm}
            variant="primary"
            isConfirmBtnDisabled={
              formikProps.isSubmitting ||
              !formikProps.isValid ||
              isSameDomainName
            }
          >
            <Form>
              <RenameDomainFormContent>
                <TextField
                  name={NAME_FIELD}
                  placeholder={formatMessage(renameDomainMessages.input)}
                  showLabel
                />
              </RenameDomainFormContent>
            </Form>
          </ConfirmModal>
        )
      }}
    </Formik>
  )
}

export default RenameDomainModal
